import { createContext, useContext, useMemo } from "react";

export const ERP_EDITIONS = {
  starter: {
    key: "starter",
    name: "Basic / Starter Edition",
    badge: "Starter",
    description: "For small shops and single-company accounting with GST billing.",
    features: [
      "Dashboard", "Accounts", "Journal", "Ledger", "Opening Balance", "Cash Book",
      "Trial Balance", "Profit & Loss", "Balance Sheet", "Final Accounts", "Day Book",
      "Items", "Customers", "Suppliers", "Invoices", "Sales Voucher", "Purchase Voucher",
      "Sales Return", "Credit Note", "Debit Note", "Receipt Voucher", "Payment Voucher", "GST Reports", "E-Way Bill",
      "Invoice Templates", "Companies"
    ],
    routePrefixes: [
      "/dashboard", "/accounts", "/journal", "/ledger", "/opening-balance", "/cash-book",
      "/trial-balance", "/profit-loss", "/balance-sheet", "/final-accounts", "/day-book",
      "/items", "/customers", "/suppliers", "/invoices", "/invoice-print", "/sales-voucher",
      "/purchase-voucher", "/sales-return", "/credit-note", "/debit-note", "/receipt-voucher", "/payment-voucher",
      "/gst-reports", "/eway-bill", "/invoice-templates", "/companies", "/company-setup", "/system-settings",
      "/unauthorized"
    ]
  },
  standard: {
    key: "standard",
    name: "Standard Edition",
    badge: "Standard",
    description: "For traders and distributors who need inventory and operational reports.",
    inherits: "starter",
    features: [
      "Stock Ledger", "Stock Summary", "Stock Adjustment", "Bank Book", "Bank Reconciliation",
      "Customer Outstanding", "Supplier Outstanding", "Contra Voucher", "GST Voucher", "Register Users"
    ],
    routePrefixes: [
      "/stock-ledger", "/stock-summary", "/stock-adjustment", "/bank-book", "/bank-reconciliation",
      "/customer-outstanding", "/supplier-outstanding", "/contra-voucher", "/gst-voucher", "/admin-users"
    ]
  },
  professional: {
    key: "professional",
    name: "Professional Edition",
    badge: "Professional",
    description: "For medium businesses needing HR, payroll, permissions, audit and year closing.",
    inherits: "standard",
    features: [
      "Employees", "Payroll", "Roles & Permissions", "Audit Logs", "Year Closing", "Edit/Delete Manager", "License Keys"
    ],
    routePrefixes: [
      "/employees", "/payroll", "/permissions", "/audit-logs", "/year-closing", "/edit-manager", "/license"
    ]
  },
  enterprise: {
    key: "enterprise",
    name: "Enterprise Edition",
    badge: "Enterprise",
    description: "For full ERP deployments with all current and future enterprise modules enabled.",
    inherits: "professional",
    features: [
      "All Modules", "Multi-branch Ready", "Approval Workflow Ready", "AI Reports Ready", "CRM Ready",
      "Barcode Ready", "E-Way Bill Ready", "WhatsApp Sharing Ready", "Mobile App Ready"
    ],
    routePrefixes: ["*"]
  }
};

const ORDER = ["starter", "standard", "professional", "enterprise"];
const EditionContext = createContext(null);

/*
  SELLER-CONTROLLED EDITION LOCK
  --------------------------------
  Clients must not select the ERP edition from the application UI.

  For each client build, set REACT_APP_LOCKED_ERP_EDITION in .env.production:
    REACT_APP_LOCKED_ERP_EDITION=starter
    REACT_APP_LOCKED_ERP_EDITION=standard
    REACT_APP_LOCKED_ERP_EDITION=professional
    REACT_APP_LOCKED_ERP_EDITION=enterprise

  If the environment variable is missing/invalid, Enterprise is used for developer safety.
*/
const rawLockedEdition = (process.env.REACT_APP_LOCKED_ERP_EDITION || "enterprise").toLowerCase();
export const LOCKED_ERP_EDITION = ERP_EDITIONS[rawLockedEdition] ? rawLockedEdition : "enterprise";

function collectEdition(key, field) {
  const index = ORDER.indexOf(key);
  return ORDER.slice(0, index + 1).flatMap((editionKey) => ERP_EDITIONS[editionKey][field] || []);
}

export function EditionProvider({ children }) {
  const value = useMemo(() => {
    const editionKey = LOCKED_ERP_EDITION;
    const edition = ERP_EDITIONS[editionKey] || ERP_EDITIONS.enterprise;
    const routePrefixes = editionKey === "enterprise" ? ["*"] : collectEdition(editionKey, "routePrefixes");
    const features = editionKey === "enterprise" ? collectEdition("enterprise", "features") : collectEdition(editionKey, "features");

    const canUseRoute = (path = "") => {
      if (routePrefixes.includes("*")) return true;
      return routePrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    };

    return {
      editionKey,
      edition,
      editions: ERP_EDITIONS,
      features,
      lockedBySeller: true,
      canUseRoute,
      setEditionKey: () => {
        console.warn("ERP edition is locked by seller build/license and cannot be changed by client UI.");
      },
    };
  }, []);

  return <EditionContext.Provider value={value}>{children}</EditionContext.Provider>;
}

export function useEdition() {
  const ctx = useContext(EditionContext);
  if (!ctx) throw new Error("useEdition must be used inside EditionProvider");
  return ctx;
}
